import React, { useState } from "react";
import { toast } from "sonner";
import { Check } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useDesignOptions } from "@/components/thankyou/DesignFields";

const PILL = "border-2 border-black px-2 py-1 text-[10px] uppercase tracking-widest inline-flex items-center gap-1 transition-colors duration-100";

// The buyer's saved layout and a one-click way to keep another one.
export function useKeepLayout(b, onSaved, onKept) {
  const [saved, setSaved] = useState(b.ty_design?.key || null);
  const [busy, setBusy] = useState(false);
  const keep = async (key) => {
    setBusy(true);
    try {
      await api.put(`/admin/thank-you/${b.id}/design`, { design: key });
      setSaved(key);
      onKept?.();
      toast.success(`${b.firm_name} now uses this layout`);
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save the layout");
    } finally {
      setBusy(false);
    }
  };
  return { saved, busy, keep };
}

// Try another thank-you layout in the preview, then keep it for this buyer with one click.
export function LayoutPicker({ b, tryKey, onTry, saved, busy, keep }) {
  const options = useDesignOptions();
  const p = `ty-preview-${b.code}-layout`;
  const shown = tryKey || saved || "";
  return (
    <div className="mb-2 border-2 border-black bg-zinc-50 p-2" data-testid={p}>
      <div className="mb-1.5 text-[11px]">
        <span className="label-mono">Layout</span>{" "}
        <span data-testid={`${p}-current`}>Now using: <b>{nameOf(options, saved, b)}</b></span>
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        {options.map((o) => (
          <button key={o.key} type="button" onClick={() => onTry(o.key === saved ? null : o.key)} title={o.description}
                  className={`${PILL} ${o.key === shown ? "bg-black text-white" : "bg-white hover:bg-zinc-100"}`} data-testid={`${p}-${o.key.replace(":", "-")}`}>
            {o.key === saved && <Check size={10} weight="bold" />} {o.name}
          </button>
        ))}
        {tryKey && tryKey !== saved && (
          <button type="button" onClick={() => keep(tryKey)} disabled={busy} data-testid={`${p}-keep`}
                  className={`${PILL} ml-auto bg-emerald-700 text-white hover:bg-emerald-800 disabled:opacity-50`}>
            {busy ? "Saving…" : `Use this layout for ${b.firm_name}`}
          </button>
        )}
      </div>
    </div>
  );
}

function nameOf(options, key, b) {
  if (!key) return `${b.ty_design?.is_default ? b.ty_design.name : "Classic"} (site default)`;
  return options.find((o) => o.key === key)?.name || key;
}
